import { Navigate } from "react-router-dom";
import Breadcrumb from "@/components/Breadcrumb";
import PageHeading from "@/components/PageHeading/PageHeading";
import { TableHeading } from "@/components/Table";
import Tabs from "@/components/Tabs";
import useCapabilities, { useCanManageApplications, useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { useParams } from "@/hooks/useSearchParams";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import type { Path } from "@/router";
import CommitteesTable from "./_components/CommitteesTable";
import { getMembershipServicesNavLinks } from "../../_membershipServicesNavLinks";
import { usePbdCardManualFulfillmentAccess } from "@/pages/people/membership-services/pbd-card-manual-fulfillment/access";

type CommitteeTab = "all" | "active" | "inactive";

const COMMITTEE_TABS: { label: string; link: Path }[] = [
  { label: "Active Committees by Term", link: "/people/membership-services/legacy-committees/active" },
  { label: "Inactive Committees by Term", link: "/people/membership-services/legacy-committees/inactive" },
  { label: "All Committees", link: "/people/membership-services/legacy-committees/all" }
];

const COMMITTEE_TAB_CONTENT: Record<CommitteeTab, { heading: string; body: string }> = {
  all: {
    heading: "All Committees",
    body: "View national committees with aggregate historical term and participant counts."
  },
  active: {
    heading: "Active Committees by Term",
    body: "View current national committee terms and their participant counts."
  },
  inactive: {
    heading: "Inactive Committees by Term",
    body: "View historical national committee terms and their participant counts."
  }
};

function committeeTabLink(link: Path, searchParamsUrl: string) {
  const searchParams = new URLSearchParams(searchParamsUrl);
  if (link.endsWith("/all")) {
    searchParams.delete("committeeDateRange");
  }

  const search = searchParams.toString();
  return `${link}${search ? `?${search}` : ""}` as Path;
}

export function CommitteeManagementPageContent({ activeTab }: { activeTab: CommitteeTab }) {
  usePageTitle(`/people/membership-services/legacy-committees/${activeTab}`, "People | Committee Management");
  const { loaded } = useCapabilities();
  const flags = useFlags();
  const canManageApplications = useCanManageApplications();
  const canViewCommittees = useCanViewCommitteesBool();
  const { canQueue } = usePbdCardManualFulfillmentAccess();
  const navLinks = getMembershipServicesNavLinks(flags, {
    canManageApplications,
    canViewCommittees,
    canQueuePbdCardManualFulfillment: canQueue
  });
  const { searchParamsUrl } = useParams();

  if (!canViewCommittees) {
    if (!loaded) {
      return null;
    }
    return <Navigate to="/people/membership-services" replace />;
  }

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Committee Management"
        backLink={{ to: "/people/membership-services" }}
      />
      <PageHeading title="Committee Management" compact />
      <Tabs
        tabItems={COMMITTEE_TABS.map((tab) => ({
          label: tab.label,
          link: committeeTabLink(tab.link, searchParamsUrl)
        }))}
      />
      <div className="w-full">
        <TableHeading heading={COMMITTEE_TAB_CONTENT[activeTab].heading} body={COMMITTEE_TAB_CONTENT[activeTab].body} />
        <CommitteesTable activeTab={activeTab} />
      </div>
    </AppLayout>
  );
}

export default function CommitteeManagementPage() {
  return <Navigate to="/people/membership-services/legacy-committees/active" replace />;
}
